import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { PDF_DESC_CLASS } from "@/lib/proposalPdfTypography";
import { PROPOSAL_ACCENT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { useState } from "react";
import ProposalItemCleoExplanation from "@/components/proposal/ProposalItemCleoExplanation";
import ProposalLineAskCleoButton from "@/components/proposal/ProposalLineAskCleoButton";
import ProposalProductImageLightbox from "@/components/proposal/ProposalProductImageLightbox";
import {
  parseCustomerExplanationStored,
  resolveProposalItemHoverExplanation,
  type CleoItemHoverExplanation,
  type ProposalItemHoverSource,
} from "@shared/proposalItemCleoDescription";
import { resolveProposalLineAskCleoLabel } from "@shared/proposalCleoInProposalExplain";

export type ProposalProductSupportLinks = {
  brochure: string | null;
  specs: string | null;
  reviews: string | null;
};

export type ProposalProductLineCardProps = {
  variant?: "retail" | "project";
  imageUrl?: string | null;
  imageAlt?: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  metaLine?: string | null;
  quantity: number | string;
  priceFormatted: string;
  internalBasePriceFormatted?: string | null;
  hidePrice?: boolean;
  badge?: string | null;
  /** Compact VO status chip: ADDED / REMOVED / UPDATED / REPLACED / REPLACEMENT */
  statusBadge?: "ADDED" | "REMOVED" | "UPDATED" | "REPLACED" | "REPLACEMENT" | null;
  /** Customer-facing VO change reason shown under annotation badges when present. */
  changeReason?: string | null;
  supportLinks?: ProposalProductSupportLinks | null;
  indent?: boolean;
  compact?: boolean;
  className?: string;
  interactiveHover?: boolean;
  /** Cleo product description — visible under the item name. */
  cleoDescription?: string | null;
  /** Cleo hover explanation JSON — shown via info icon. */
  customerExplanation?: string | null;
  /** Staff preview: hint when Cleo descriptions are still being fetched. */
  showCleoDescriptionPending?: boolean;
  /** Override default pending hint copy (staff sandbox). */
  cleoDescriptionPendingMessage?: string;
  /** Staff preview: warn when fallback hover copy is shown instead of Cleo JSON. */
  showStaffHoverDiagnostics?: boolean;
  staffHoverFallbackMessage?: string;
  /** Customer view: click thumbnail to enlarge. Ignored when image is missing. */
  enableImageLightbox?: boolean;
  /** Tighter body copy for curated PDF print package. */
  compactCopy?: boolean;
  /** Luxury PDF primary card — brand/model/display name hierarchy and larger image. */
  pdfPremiumLayout?: boolean;
  productBrand?: string | null;
  productModel?: string | null;
  productDisplayName?: string | null;
  /**
   * Staff Link A Ask Cleo — visible `?` opens the proposal chat with a prefilled
   * "What is the [label] for?" question. When set, this is the primary explain path
   * (not hover-only).
   */
  onAskCleo?: (question: string) => void;
};

function SupportLinksRow({ links }: { links: ProposalProductSupportLinks }) {
  const LinkPart = ({ label, href }: { label: string; href: string | null }) => (
    <a
      href={href ?? "#"}
      target={href ? "_blank" : undefined}
      rel={href ? "noopener noreferrer" : undefined}
      onClick={(e) => {
        if (!href) e.preventDefault();
      }}
      className="hover:underline"
      aria-disabled={!href}
    >
      {label}
    </a>
  );
  return (
    <div className="text-xs font-medium tracking-wide" style={{ color: PROPOSAL_ACCENT }}>
      <LinkPart label="BROCHURE" href={links.brochure} />
      <span className="mx-2 opacity-60">|</span>
      <LinkPart label="SPECS" href={links.specs} />
      <span className="mx-2 opacity-60">|</span>
      <LinkPart label="REVIEWS" href={links.reviews} />
    </div>
  );
}

export default function ProposalProductLineCard({
  variant = "retail",
  imageUrl,
  imageAlt = "Product image",
  title,
  subtitle,
  description,
  metaLine,
  quantity,
  priceFormatted,
  internalBasePriceFormatted,
  hidePrice = false,
  badge,
  statusBadge = null,
  changeReason = null,
  supportLinks,
  indent,
  compact,
  className,
  interactiveHover,
  cleoDescription,
  customerExplanation,
  showCleoDescriptionPending = false,
  cleoDescriptionPendingMessage = "Cleo is fetching product descriptions…",
  showStaffHoverDiagnostics = false,
  staffHoverFallbackMessage = "",
  enableImageLightbox = false,
  compactCopy = false,
  pdfPremiumLayout = false,
  productBrand,
  productModel,
  productDisplayName,
  onAskCleo,
}: ProposalProductLineCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const imageTrimmed = String(imageUrl ?? "").trim();
  const isProject = variant === "project";
  // Proposal-specific Description wins; Cleo Description is the intelligent fallback.
  const visibleDescription = String(description ?? cleoDescription ?? "").trim();

  let hoverExplanation: CleoItemHoverExplanation | null = null;
  let hoverSource: ProposalItemHoverSource | null = null;

  if (isProject) {
    const resolved = resolveProposalItemHoverExplanation({
      customerExplanation,
      visibleDescription,
      productTitle: title,
    });
    hoverExplanation = resolved.explanation;
    hoverSource = resolved.source;
  } else {
    hoverExplanation = parseCustomerExplanationStored(customerExplanation);
  }

  const showCleoPending = showCleoDescriptionPending && !visibleDescription;
  // Never expose internal Cleo/fallback diagnostics on customer or staff proposal surfaces.
  const showHoverFallbackHint = false;
  void showStaffHoverDiagnostics;
  void staffHoverFallbackMessage;
  void hoverSource;
  const canOpenLightbox = enableImageLightbox && Boolean(imageTrimmed);
  const voChangeReason = String(changeReason ?? "").trim() || null;

  const renderProductImage = (imgClassName: string, wrapperClassName?: string) => {
    const img = (
      <img
        src={imageTrimmed}
        alt={isProject ? "" : imageAlt}
        className={cn(imgClassName, canOpenLightbox && "transition-transform duration-200 group-hover:scale-[1.03]")}
        loading="lazy"
      />
    );
    if (!canOpenLightbox) {
      return wrapperClassName ? <div className={wrapperClassName}>{img}</div> : img;
    }
    return (
      <>
        <button
          type="button"
          className={cn(
            "group shrink-0 rounded-lg border-0 bg-transparent p-0 text-left",
            "cursor-pointer transition-shadow duration-200",
            "hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8A96A]/60",
            "ring-2 ring-transparent hover:ring-[#C8A96A]/35",
            wrapperClassName,
          )}
          aria-label={`View enlarged image for ${title}`}
          title="Click to enlarge"
          data-testid="proposal-product-image-trigger"
          onClick={() => setLightboxOpen(true)}
        >
          {img}
        </button>
        <ProposalProductImageLightbox
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
          src={imageTrimmed}
          alt={imageAlt}
        />
      </>
    );
  };

  const imageColumn: ReactNode | null = imageTrimmed
    ? isProject
      ? renderProductImage(
          pdfPremiumLayout
            ? "h-[120px] w-[160px] rounded-md border border-[#1A2744]/10 bg-[#FAFAF7] object-contain"
            : "h-24 w-24 rounded-lg border border-[#1A2744]/10 bg-[#FAFAF7] object-contain",
          pdfPremiumLayout ? "shrink-0" : undefined,
        )
      : renderProductImage(
          "h-full w-full object-contain",
          "h-60 overflow-hidden rounded-md border border-[#1A2744]/15 bg-white sm:h-64",
        )
    : null;

  const cardClass = cn(
    isProject
      ? pdfPremiumLayout
        ? "pdf-keep-together rounded-lg border border-[#1A2744]/12 bg-white"
        : "rounded-xl border border-[#1A2744]/10 bg-white/90 shadow-sm"
      : "border-[#1A2744]/10 bg-white/60",
    indent && isProject && "ml-4 border-l-2 border-l-[#C8A96A]/50",
    interactiveHover &&
      !isProject &&
      "transition-[box-shadow,background-color] duration-150 hover:bg-white/85 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A2744]/25",
    className,
  );

  const showPdfNameHierarchy =
    pdfPremiumLayout && Boolean(productBrand || productModel || productDisplayName || title);

  const pdfProminentName = productDisplayName
    ? productDisplayName
    : productBrand && productModel
      ? `${productBrand} ${productModel}`
      : title;

  const askCleoLabel = resolveProposalLineAskCleoLabel({
    brand: productBrand,
    model: productModel,
    name: title,
    displayName: productDisplayName,
    title,
  });
  const askCleoControl = onAskCleo ? (
    <ProposalLineAskCleoButton label={askCleoLabel} onAsk={onAskCleo} />
  ) : null;
  const hoverControl =
    !onAskCleo && hoverExplanation ? <ProposalItemCleoExplanation explanation={hoverExplanation} /> : null;
  const lineExplainControl = askCleoControl ?? hoverControl;

  const titleBlock = showPdfNameHierarchy ? (
    <div className="min-w-0 space-y-0.5">
      {productDisplayName && productBrand ? (
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#4B5563]">{productBrand}</p>
      ) : null}
      {productDisplayName && productModel ? (
        <p className="text-[11px] font-medium text-[#374151]">{productModel}</p>
      ) : null}
      {pdfProminentName ? (
        <p className="text-[13px] font-semibold leading-snug text-[#1A2744]">{pdfProminentName}</p>
      ) : null}
      {lineExplainControl}
    </div>
  ) : (title || (isProject && hoverExplanation) || askCleoControl) ? (
    <div className="flex items-start gap-1.5">
      {title ? (
        <p
          className={cn(
            isProject ? "font-semibold leading-snug text-[#1A2744]" : "whitespace-pre-line font-medium",
            pdfPremiumLayout && isProject && "text-[13px]",
            "min-w-0 flex-1",
          )}
        >
          {title}
        </p>
      ) : (
        <span className="sr-only">Proposal item</span>
      )}
      {lineExplainControl}
    </div>
  ) : null;

  const body = (
    <div
      className={cn(
        isProject
          ? cn("flex", imageColumn && (pdfPremiumLayout ? "gap-3" : "gap-4"))
          : imageColumn
            ? "grid grid-cols-1 gap-4 sm:grid-cols-[280px_1fr] sm:items-start"
            : "block",
        pdfPremiumLayout && isProject && "p-3",
        compact && isProject && !pdfPremiumLayout && "p-3",
        !compact && isProject && !pdfPremiumLayout && "p-4",
      )}
      data-testid={pdfPremiumLayout ? "dtools-pdf-primary-product-card" : undefined}
    >
      {imageColumn}
      <div
        className={cn(
          "min-w-0 space-y-2",
          isProject && "flex flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between",
        )}
      >
        <div className={cn("min-w-0 space-y-1.5", isProject && "flex-1")}>
          {titleBlock}
          {showCleoPending ? (
            <p className="text-xs italic text-[#1A2744]/50">{cleoDescriptionPendingMessage}</p>
          ) : null}
          {showHoverFallbackHint ? (
            <p className="text-xs italic text-amber-800/80">{staffHoverFallbackMessage}</p>
          ) : null}
          {subtitle && !showPdfNameHierarchy ? (
            <p className={pdfPremiumLayout ? "text-[11px] text-[#4B5563]" : "text-sm text-[#1A2744]/80"}>{subtitle}</p>
          ) : null}
          {visibleDescription ? (
            <p
              className={cn(
                "break-words [overflow-wrap:anywhere]",
                pdfPremiumLayout
                  ? PDF_DESC_CLASS
                  : compactCopy
                    ? "whitespace-pre-wrap text-xs leading-5 text-[#374151]"
                    : "whitespace-pre-wrap text-sm leading-relaxed text-[#374151]",
              )}
            >
              {visibleDescription}
            </p>
          ) : null}
          {metaLine ? <p className="text-xs text-[#1A2744]/60">{metaLine}</p> : null}
          {statusBadge ? (
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] font-semibold tracking-wide",
                statusBadge === "REMOVED" && "border-red-300 text-red-700",
                statusBadge === "ADDED" && "border-emerald-300 text-emerald-800",
                statusBadge === "UPDATED" && "border-[#C8A96A]/70 text-[#1A2744]",
              )}
            >
              {statusBadge}
            </Badge>
          ) : null}
          {badge ? (
            <Badge variant="outline" className="text-[10px] font-normal">
              {badge}
            </Badge>
          ) : null}
          {voChangeReason ? (
            <p
              className={cn(
                "text-xs leading-5 text-[#374151]",
                pdfPremiumLayout && "text-[11px] text-[#4B5563]",
              )}
              data-testid="vo-line-change-reason"
            >
              <span className="font-medium text-[#1A2744]">Reason: </span>
              {voChangeReason}
            </p>
          ) : null}
          {supportLinks ? <SupportLinksRow links={supportLinks} /> : null}
          {!isProject && !hidePrice ? (
            <div className="flex items-center justify-between text-sm opacity-90">
              <p className="text-[#1A2744]/75">Qty: {quantity}</p>
              <p className="text-right font-medium" style={{ color: PROPOSAL_TEXT }}>
                {priceFormatted}
              </p>
            </div>
          ) : !isProject ? (
            <p className="text-sm text-[#1A2744]/75">Qty: {quantity}</p>
          ) : null}
        </div>
        {isProject ? (
          <div
            className={cn(
              "shrink-0 text-right tabular-nums",
              pdfPremiumLayout
                ? "self-start px-1 pt-0.5 text-[11px] text-[#4B5563]"
                : "rounded-lg border border-[#1A2744]/10 bg-[#FAFAF7] px-3 py-2 text-sm",
            )}
          >
            <p className={pdfPremiumLayout ? "" : "text-[#4B5563]"}>
              Qty: <span className="font-semibold text-[#1A2744]">{quantity}</span>
            </p>
            {!hidePrice && !pdfPremiumLayout ? (
              <div>
                <p className="mt-0.5 font-semibold text-[#1A2744]">{priceFormatted}</p>
                {internalBasePriceFormatted ? (
                  <p className="text-xs font-bold text-[#1A2744]/65" data-testid="internal-base-price">
                    † {internalBasePriceFormatted}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (isProject) {
    return <div className={cardClass}>{body}</div>;
  }

  return (
    <Card className={cardClass}>
      <CardContent className="p-4">{body}</CardContent>
    </Card>
  );
}
